import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { apiClient } from "../../services/authService";
import {
  PaymentConfig,
  DEFAULT_PAYMENT_CONFIG,
} from "../PaymentRequest";
import { LbpSettings, DEFAULT_LBP_SETTINGS, getLbpSettings } from "../../utils/currency";

const LBP_STORAGE_KEY = "lbp_rate_config";

const AdminSettings: React.FC = () => {
  const [saved, setSaved]           = useState(false);
  const [saveError, setSaveError]   = useState("");
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig>(DEFAULT_PAYMENT_CONFIG);
  const [lbpSettings, setLbpSettings] = useState<LbpSettings>(DEFAULT_LBP_SETTINGS);

  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

  useEffect(() => {
    // Load saved payment config: try API first, then localStorage
    apiClient.get<{ value: PaymentConfig }>("/settings/payment-methods")
      .then(res => { if (res.data?.value) setPaymentConfig(res.data.value); })
      .catch(() => {
        const local = localStorage.getItem("payment_methods_config");
        if (local) { try { setPaymentConfig(JSON.parse(local)); } catch {} }
      });
    getLbpSettings().then(setLbpSettings);
  }, []);

  const updateMethod = <K extends keyof PaymentConfig>(
    method: K,
    field: keyof PaymentConfig[K],
    value: any,
  ) => {
    setPaymentConfig(prev => ({
      ...prev,
      [method]: { ...prev[method], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaveError("");
    // Always persist to localStorage so PaymentRequest can read it
    localStorage.setItem("payment_methods_config", JSON.stringify(paymentConfig));
    localStorage.setItem(LBP_STORAGE_KEY, JSON.stringify(lbpSettings));
    // Also try to persist to backend
    try {
      await apiClient.put("/settings/payment-methods", { value: paymentConfig });
    } catch {
      // Backend endpoint may not exist — localStorage fallback is enough
    }
    try {
      await apiClient.put("/settings/lbp-rate", { value: lbpSettings });
    } catch {
      // Backend endpoint may not exist — localStorage fallback is enough
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Settings saved successfully.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSaveError("")}>
          {saveError}
        </Alert>
      )}

      {/* API Configuration */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          API Configuration
        </Typography>
        <TextField
          label="API Base URL"
          fullWidth
          value={apiBaseUrl}
          disabled
          helperText="Set via REACT_APP_API_BASE_URL environment variable"
          sx={{ mb: 2 }}
        />
        <Chip label="Connected" color="success" size="small" sx={{ fontWeight: 600 }} />
      </Paper>

      {/* Payment Methods */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Payment Methods
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Configure the account numbers shown to users when they subscribe. Toggle each method on or off.
        </Typography>

        {/* Wish Money */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: "1.3rem" }}>💜</Typography>
              <Typography fontWeight={700}>Wish Money</Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentConfig.wish_money.active}
                  onChange={e => updateMethod("wish_money", "active", e.target.checked)}
                  color="primary"
                />
              }
              label={paymentConfig.wish_money.active ? "Active" : "Hidden"}
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Stack>
          <TextField
            fullWidth
            label="Wish Money Number"
            placeholder="+961 XX XXX XXX"
            value={paymentConfig.wish_money.number}
            onChange={e => updateMethod("wish_money", "number", e.target.value)}
            disabled={!paymentConfig.wish_money.active}
            size="small"
          />
        </Paper>

        {/* OMT */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: "1.3rem" }}>🟠</Typography>
              <Typography fontWeight={700}>OMT</Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentConfig.omt.active}
                  onChange={e => updateMethod("omt", "active", e.target.checked)}
                  color="primary"
                />
              }
              label={paymentConfig.omt.active ? "Active" : "Hidden"}
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Stack>
          <TextField
            fullWidth
            label="OMT Number"
            placeholder="+961 XX XXX XXX"
            value={paymentConfig.omt.number}
            onChange={e => updateMethod("omt", "number", e.target.value)}
            disabled={!paymentConfig.omt.active}
            size="small"
          />
        </Paper>

        {/* Bank Transfer */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: "1.3rem" }}>🏦</Typography>
              <Typography fontWeight={700}>Bank Transfer</Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentConfig.bank.active}
                  onChange={e => updateMethod("bank", "active", e.target.checked)}
                  color="primary"
                />
              }
              label={paymentConfig.bank.active ? "Active" : "Hidden"}
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Stack>
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              label="Bank Name"
              placeholder="e.g. Byblos Bank"
              value={paymentConfig.bank.bankName}
              onChange={e => updateMethod("bank", "bankName", e.target.value)}
              disabled={!paymentConfig.bank.active}
              size="small"
            />
            <TextField
              fullWidth
              label="Account Holder Name"
              placeholder="e.g. Dr. Ahmad Sadaka"
              value={paymentConfig.bank.accountName}
              onChange={e => updateMethod("bank", "accountName", e.target.value)}
              disabled={!paymentConfig.bank.active}
              size="small"
            />
            <TextField
              fullWidth
              label="IBAN / Account Number"
              placeholder="LB00 0000 0000 0000 0000 0000 0000"
              value={paymentConfig.bank.iban}
              onChange={e => updateMethod("bank", "iban", e.target.value)}
              disabled={!paymentConfig.bank.active}
              size="small"
            />
          </Stack>
        </Paper>

        {/* WhatsApp / Cash */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: "1.3rem" }}>💬</Typography>
              <Typography fontWeight={700}>WhatsApp (Cash)</Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={paymentConfig.whatsapp.active}
                  onChange={e => updateMethod("whatsapp", "active", e.target.checked)}
                  color="primary"
                />
              }
              label={paymentConfig.whatsapp.active ? "Active" : "Hidden"}
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Stack>
          <TextField
            fullWidth
            label="WhatsApp Number"
            placeholder="+961 XX XXX XXX"
            value={paymentConfig.whatsapp.number}
            onChange={e => updateMethod("whatsapp", "number", e.target.value)}
            disabled={!paymentConfig.whatsapp.active}
            size="small"
          />
        </Paper>
      </Paper>

      {/* Currency Display */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Currency Display
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Show an approximate LBP conversion next to USD prices on pricing and payment pages. Billing always stays in USD — this is a display-only conversion using a rate you set manually.
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: "1.3rem" }}>🇱🇧</Typography>
              <Typography fontWeight={700}>Show LBP Conversion</Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={lbpSettings.showLbp}
                  onChange={e => setLbpSettings(prev => ({ ...prev, showLbp: e.target.checked }))}
                  color="primary"
                />
              }
              label={lbpSettings.showLbp ? "Active" : "Hidden"}
              labelPlacement="start"
              sx={{ m: 0 }}
            />
          </Stack>
          <TextField
            fullWidth
            type="number"
            label="Exchange Rate (LBP per 1 USD)"
            placeholder="89000"
            value={lbpSettings.rate}
            onChange={e => setLbpSettings(prev => ({ ...prev, rate: Number(e.target.value) || 0 }))}
            disabled={!lbpSettings.showLbp}
            size="small"
            helperText="Update this periodically to reflect the current parallel market rate"
          />
        </Paper>
      </Paper>

      {/* Cloudflare Stream */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Cloudflare Stream
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Configure Cloudflare Stream for video hosting. These settings are
          managed in the backend <code>.env</code> file.
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Account ID" fullWidth placeholder="Enter Cloudflare Account ID" disabled
            helperText="CLOUDFLARE_ACCOUNT_ID — configured in backend .env" />
          <TextField label="Stream API Token" fullWidth type="password" placeholder="••••••••••••" disabled
            helperText="CLOUDFLARE_STREAM_API_TOKEN — configured in backend .env" />
          <FormControlLabel control={<Switch disabled />}
            label="Enable Cloudflare Stream (ENABLE_CLOUDFLARE_STREAM)" />
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <strong>How to connect:</strong>
            <br />
            1. Subscribe to Cloudflare Stream at{" "}
            <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer">
              dash.cloudflare.com
            </a>
            <br />
            2. Get your Account ID and create an API token with Stream permissions
            <br />
            3. Set <code>CLOUDFLARE_ACCOUNT_ID</code>, <code>CLOUDFLARE_STREAM_API_TOKEN</code>,
            and <code>ENABLE_CLOUDFLARE_STREAM=true</code> in backend <code>.env</code>
            <br />
            4. Restart the backend — everything works immediately
          </Alert>
        </Box>
      </Paper>

      {/* General Settings */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 3px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          General
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Site Name"       fullWidth defaultValue="Dr.Paw" />
          <TextField label="Support Email"   fullWidth defaultValue="support@drpawleb.com" />
          <TextField label="Default Currency" fullWidth defaultValue="USD" sx={{ maxWidth: 200 }} />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          sx={{
            bgcolor: "#1565C0",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            "&:hover": { bgcolor: "#0D47A1" },
          }}
        >
          Save Settings
        </Button>
      </Box>
    </Box>
  );
};

export default AdminSettings;
